/**
 * ============================================================================
 * useSettings - PRODUCTION-READY SETTINGS HOOKS
 * ============================================================================
 *
 * TanStack Query hooks for Settings with:
 * - Smart caching (bank details cached for 15min, no polling needed)
 * - Separate mutations for profile, password, account deletion
 * - Proper error handling and loading states
 * - AuthContext integration for profile updates
 *
 * CACHING STRATEGY:
 * - Bank details: 15min cache (rarely changes)
 * - Profile: Managed by AuthContext (no separate query)
 * - Mutations invalidate relevant queries automatically
 *
 * NOTE: Settings data is static - NO POLLING NEEDED
 * ============================================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import vendorApi from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type {
  ProfileData,
  PasswordChangeData,
  BankDetailsResponse,
  AccountDeletionRequest,
} from '../types/settings';

/**
 * Query hook for bank details
 * Cached for 15 minutes since bank details rarely change
 */
export function useBankDetails() {
  const { user } = useAuth();

  return useQuery<BankDetailsResponse, Error>({
    queryKey: ['bankDetails', user?.id],

    queryFn: async () => {
      return await vendorApi.getBankDetails();
    },

    enabled: !!user?.id,
    staleTime: 15 * 60 * 1000, // 15 minutes - bank details don't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes in cache
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}

/**
 * Mutation hook for updating profile
 * Automatically updates AuthContext with new user data
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const { setUser } = useAuth();

  return useMutation({
    mutationFn: async (data: Partial<ProfileData>) => {
      return await vendorApi.updateProfile(data);
    },

    onSuccess: (updatedUser: any) => {
      // Update AuthContext with new user data
      if (setUser) {
        setUser(updatedUser);
      }

      // Invalidate user queries if any
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },

    // Log only — do NOT re-throw. The caller awaits mutateAsync in a try/catch
    // and shows the error inline; re-throwing here surfaces as an uncaught
    // runtime error (the React dev-overlay crash).
    onError: (error: any) => {
      console.error('Failed to update profile:', error);
    },
  });
}

/**
 * Mutation hook for changing password
 * No cache invalidation needed (password not cached)
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: async (data: PasswordChangeData) => {
      return await vendorApi.changePassword({
        current_password: data.current_password,
        new_password: data.new_password,
        new_password_confirmation: data.new_password_confirmation,
      });
    },

    onError: (error: any) => {
      console.error('Failed to change password:', error);
    },
  });
}

/**
 * Mutation hook for requesting account deletion
 * This is a request - actual deletion handled by admins
 */
export function useRequestAccountDeletion() {
  return useMutation({
    mutationFn: async (data: AccountDeletionRequest) => {
      return await vendorApi.requestAccountDeletion(data);
    },

    onError: (error: any) => {
      console.error('Failed to request account deletion:', error);
    },
  });
}
